import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import CropperComponent from '@features/trend-research/components/common/CropperComponent';
import UploadTrendResearchImage from '@features/trend-research/components/common/imageSearch/UploadTrendResearchImage';
import ProductModal from '@features/trend-research/components/common/ProductModal';
import AddMoodBoard from '@features/trend-research/components/moodboard/addMoodboard';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	findAllImageSearchHistory,
	findAllImageSearchResult,
	saveImageSearchHistory
} from '@services/trendResearch';

import { isPageReachBottom } from '@utils/Util';

const ImageSearch = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const sourceRef = useRef(null);
	const productRef = useRef(null);
	const loadingRef = useRef(false);
	const base64StrRef = useRef(null);
	const [source, setSource] = useState(null);
	const [products, setProducts] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [historyImages, setHistoryImages] = useState([]);
	const [selectedImage, setSelectedImage] = useState(null);
	const [isAddToMoodBoard, setIsAddToMoodBoard] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [isOpenMoodBoardModal, setIsOpenMoodBoardModal] = useState(false);
	const [selectedMoodBoardProduct, setSelectedMoodBoardProduct] = useState(null);

	const onClose = () => {
		setShowModal(false);
		setSelectedProductId(null);
	};

	useEffect(() => {
		sourceRef.current = location.state.source.toUpperCase();
		setSource(location.state.source.toUpperCase());
	}, [location.state]);

	const getAllImageSearchHistory = async (isPreset = false) => {
		try {
			dispatch(openLoader());
			const data = await findAllImageSearchHistory(`?size=1000&page=0`);
			setHistoryImages(data.data);
			if (isPreset && data?.data?.length > 0) {
				setSelectedImage(data.data[0]);
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		getAllImageSearchHistory(true);
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const getImageSearchResult = async (imgStr, page = 0) => {
		try {
			loadingRef.current = true;
			dispatch(openLoader());
			const data = await findAllImageSearchResult(
				{ base64Str: imgStr },
				`?source=${sourceRef.current}&size=20&page=${page}`
			);
			if (data.currentPage > 0) {
				setProducts((prev) => [...prev, ...productRef.current.data]);
			} else {
				setProducts(data.data);
			}
			productRef.current = data;
			loadingRef.current = false;
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};

	const handleScroll = async () => {
		if (isPageReachBottom() && !loadingRef.current && productRef.current !== null) {
			const { data, totalElements, currentPage } = productRef.current;
			if (totalElements > 0 && totalElements !== data.length) {
				await getImageSearchResult(base64StrRef.current, currentPage + 1);
			}
		}
	};

	const renderHistoryImages = () => {
		return historyImages?.map((item) => {
			return (
				<div
					className="single-img cursor-pointer radius mr-12 mb-12"
					key={`history_image_${item.id}`}
					onClick={() => setSelectedImage(item)}
				>
					<img className={selectedImage?.id === item.id ? 'active' : ''} src={item.docUrl} alt="" />
				</div>
			);
		});
	};

	const onChangeCropHandler = async (base64String) => {
		base64StrRef.current = base64String;
		await getImageSearchResult(base64String);
	};

	const renderProducts = () => {
		return products?.map((item) => {
			return (
				<div className="single-design-card" key={`product_${item.id}`}>
					<div className="card-top">
						<img src={item.imageUrl} alt="card-image" />
						<div className="card-overly d-flex flex-column justify-content-between">
							<div className="logo-and-fab d-flex justify-content-between">
								{source === 'BRAND' && (
									<div className="brand-logo">
										{item?.brandLogoDocResponse?.docUrl && (
											<img src={item?.brandLogoDocResponse?.docUrl} alt="dd" />
										)}
									</div>
								)}
								{source !== 'BRAND' && <div />}
								<div className="custom-chekbox">
									<div className="form-group d-flex">
										{selectedMoodBoardProduct?.id !== item.id && (
											<img
												src={`${process.env.PUBLIC_URL}/icons/not-favorite.svg`}
												alt="not-favourite"
												className="add-to-favorite cursor-pointer"
												onClick={() => handleMoodboardClick(item)}
											/>
										)}
										{selectedMoodBoardProduct?.id === item.id && (
											<img
												src={`${process.env.PUBLIC_URL}/icons/favorite.svg`}
												alt="not-favourite"
												className="add-to-favorite cursor-pointer"
											/>
										)}
									</div>
								</div>
							</div>
							<div className="bottom-content text-left">
								{source === 'INSTAGRAM' && (
									<span
										className="status red-1 radius weight-500 gray_dark_01 mb-2"
										onClick={() => window.open(item.postUrl, '_blank')}
									>
										<img
											src={`${process.env.PUBLIC_URL}/icons/arrow_forward_dark.svg`}
											width={16}
											height={16}
										/>
										Instagram
									</span>
								)}
								<p className="design-title semibold-14 text-white">{item.name}</p>
								{source === 'BRAND' && (
									<ul className="common-list-component mt-1">
										<li className="text-white">{item?.price}</li>
										<li className="text-white">{item?.brandName}</li>
									</ul>
								)}
							</div>
						</div>
					</div>
					<div
						className="card-content"
						onClick={() => {
							setSelectedProductId(item.id);
							setShowModal(true);
						}}
					>
						<h3 className="semibold-14 design-title">{item.name}</h3>
						{source === 'BRAND' && (
							<ul className="common-list-component mt-1">
								<li>{item?.price}</li>
								<li>{item?.brandName}</li>
							</ul>
						)}
					</div>
					<div className="skeleton-img d-none">
						<img src={`${process.env.PUBLIC_URL}/images/trend/skeleton.gif`} alt="" />
					</div>
				</div>
			);
		});
	};

	const doFileSelection = async (fileObj) => {
		if (fileObj?.name) {
			try {
				dispatch(openLoader());
				await saveImageSearchHistory(fileObj);
				await getAllImageSearchHistory();
				dispatch(closeLoader());
			} catch {
				dispatch(closeLoader());
			}
		}
	};

	const handleMoodboardClick = async (product) => {
		setIsAddToMoodBoard(true);
		setIsOpenMoodBoardModal(true);
		setSelectedMoodBoardProduct(product);
	};

	return (
		<>
			<div className="top-header ">
				<div className="trend-list">
					<div className="page-title-area pb-2 d-flex align-items-center">
						<div className="d-flex cursor-pointer" onClick={() => navigate(-1)}>
							<img src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`} alt="" />
							<h1 className="semibold-16 pl-2">IMAGE SEARCH</h1>
						</div>
					</div>
				</div>
			</div>
			<section className="trend-data-container">
				<div className="trend-search-image-container pt-3">
					<div className="visiul-img-item">
						<div className="crop-img">
							<div className="img-upload">
								{selectedImage?.docUrl && (
									<div className="crop-image-container">
										<CropperComponent
											imageSrc={selectedImage.docUrl}
											className="w-full h-full object-contain bg-image"
											callback={onChangeCropHandler}
										/>
									</div>
								)}
							</div>
							<div className="prev-img d-flex flex-wrap pt-3">
								<UploadTrendResearchImage
									className="file-uploader small upload-design-image mr-12"
									doFileSelection={doFileSelection}
								/>
								{renderHistoryImages()}
							</div>
						</div>
						<div className="searching-img-result">
							<div className="searching-img-header">
								<p className="regular-14 weight-500 gray_dark_01 pb-1">Visual Matches</p>
								<p className="regular-12 gray_dark_02">
									Showing similar results to your photo.
								</p>
							</div>
							<div className="visual-card-item pt-3">{renderProducts()}</div>
							{products.length === 0 && (
								<div className="trending-empty-section d-flex flex-column justify-content-center align-items-center h-50">
									<div className="content text-center">
										<p className="medium-14 gray_dark_01">No results found.</p>
										<p className="regular-12 gray_dark_01 pt-2">
											Please try a different Image search
										</p>
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			</section>

			{isOpenMoodBoardModal && (
				<AddMoodBoard
					isAddToMoodBoard={isAddToMoodBoard}
					setIsAddToMoodBoard={setIsAddToMoodBoard}
					isOpenMoodBoardModal={isOpenMoodBoardModal}
					setIsOpenMoodBoardModal={setIsOpenMoodBoardModal}
					selectedProduct={selectedMoodBoardProduct}
				/>
			)}
			{showModal && selectedProductId && <ProductModal onClose={onClose} id={selectedProductId} />}
		</>
	);
};

export default ImageSearch;
